import { initializeApp, FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator, Auth } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator, Firestore } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator, Functions } from "firebase/functions";

// =============================================================================
// FIREBASE CONFIGURATION
// =============================================================================

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const hasValidConfig =
  firebaseConfig.apiKey &&
  firebaseConfig.authDomain &&
  firebaseConfig.projectId &&
  firebaseConfig.storageBucket &&
  firebaseConfig.messagingSenderId &&
  firebaseConfig.appId;

/** Shown in UI when frontend/.env is missing VITE_FIREBASE_* vars. */
export const firebaseConfigError: string | null = hasValidConfig
  ? null
  : "Missing Firebase config. Add VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID, VITE_FIREBASE_STORAGE_BUCKET, VITE_FIREBASE_MESSAGING_SENDER_ID, VITE_FIREBASE_APP_ID to frontend/.env (see frontend/.env.example). Get values from Firebase Console → Project Settings → Your apps → Web app config.";

// =============================================================================
// INITIALIZE FIREBASE (only when config is present)
// =============================================================================

let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let db: Firestore | undefined;
let functionsInstance: Functions | undefined;

if (hasValidConfig) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  functionsInstance = getFunctions(app, "us-central1");
  // Instrumentation: confirm functions region (for callable URL)
  console.log("[firebase] functions region", (functionsInstance as { _region?: string })?._region ?? "us-central1");

  if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === "true") {
    console.log("🔧 Connecting to Firebase emulators...");
    connectAuthEmulator(auth!, "http://localhost:9099", { disableWarnings: true });
    connectFirestoreEmulator(db!, "localhost", 8080);
    connectFunctionsEmulator(functionsInstance!, "localhost", 5001);
    console.log("✅ Connected to Firebase emulators");
  }
}

export { app, auth, db, functionsInstance as functions };
export default app;
